import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CopyOutlined,
  DeleteOutlined,
  KeyOutlined,
  LockOutlined,
  PlusOutlined,
  ReloadOutlined
} from "@ant-design/icons";
import { Alert, Button, Checkbox, Input, Modal, Space, Spin, Tag, Tooltip, Typography, message } from "antd";
import { serverFetch } from "../../../core/server";
import SprSotr from "./spr-sotr";
import "../database-blocks.css";
import "./spr-sotrs.css";

const getId = (row) => row.idSotr ?? row.IdSotr;

const isRowDeleted = (row) => row?.isDel === true || row?.IsDel === true;

const field = (row, camel, pascal) => row?.[camel] ?? row?.[pascal];

const SprSotrs = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [passwordUserId, setPasswordUserId] = useState(null);
  const [password, setPassword] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await serverFetch("app/database/sotrs", { method: "GET" });
      setItems(Array.isArray(result) ? result : []);
    } catch (e) {
      setItems([]);
      setError(e.message || "Не удалось загрузить пользователей");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const openNew = () => {
    setEditingId(null);
    setEditorOpen(true);
  };

  const openEdit = (id) => {
    if (!id) return;
    setEditingId(id);
    setEditorOpen(true);
  };

  const deleteSotr = (id) => {
    Modal.confirm({
      title: "Удалить пользователя?",
      okText: "Удалить",
      okButtonProps: { danger: true },
      cancelText: "Отмена",
      onOk: async () => {
        await serverFetch("app/database/sotrs", { method: "DELETE", bodyData: { idSotr: id } });
        await loadItems();
      }
    });
  };

  const copySotr = async (id) => {
    const copied = await serverFetch("app/database/sotrs/copy", { method: "POST", bodyData: { idSotr: id } });
    message.success("Пользователь скопирован");
    await loadItems();
    openEdit(copied?.idSotr ?? copied?.IdSotr ?? id);
  };

  const savePassword = async () => {
    if (!passwordUserId || passwordUserId <= 0) return;
    await serverFetch("app/database/sotrs/password", {
      method: "POST",
      bodyData: { idSotr: passwordUserId, password }
    });
    setPassword("");
    setPasswordUserId(null);
    setPasswordOpen(false);
    message.success("Пароль изменен");
    await loadItems();
  };

  const displayItems = useMemo(() => {
    if (showDeleted) return items;
    return items.filter((row) => !isRowDeleted(row));
  }, [items, showDeleted]);

  return (
    <div className="spr-sotrs">
      <Space wrap size={8} className="db-toolbar">
        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openNew}>
          Добавить
        </Button>
        <Button size="small" icon={<ReloadOutlined />} onClick={loadItems} loading={loading} />
        <Checkbox checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)}>
          Показывать удаленных
        </Checkbox>
      </Space>

      {error ? <Alert type="error" message={error} showIcon /> : null}

      <Spin spinning={loading}>
        {displayItems.length === 0 && !loading ? (
          <Typography.Paragraph type="secondary" className="db-empty">
            Пользователи не найдены
          </Typography.Paragraph>
        ) : (
          <div className="db-blocks db-blocks--grid">
            {displayItems.map((row) => {
              const id = getId(row);
              const name = field(row, "name", "Name") || "—";
              const fullName = field(row, "fullName", "FullName") || "";
              const level = field(row, "userLevel", "UserLevel");
              const depno = field(row, "depno", "Depno");
              const pwdEmpty = row?.passwordEmpty === true || row?.PasswordEmpty === true;
              const deleted = isRowDeleted(row);

              return (
                <div
                  key={String(id)}
                  className={`db-block${deleted ? " db-block--deleted" : ""}`}
                  onClick={() => openEdit(id)}
                >
                  <div className="db-block__body">
                    <div className="db-block__title">
                      <span className="db-block__title-text">{name}</span>
                      {!pwdEmpty ? (
                        <Tooltip title="Задан пароль">
                          <LockOutlined className="db-block__lock" />
                        </Tooltip>
                      ) : null}
                    </div>
                    <div className="db-block__meta">
                      {fullName && fullName !== name ? <span>{fullName}</span> : null}
                      {level !== undefined && level !== null && level !== "" ? <span>Уровень {level}</span> : null}
                      {depno !== undefined && depno !== null && depno !== "" ? <span>Отдел {depno}</span> : null}
                      {deleted ? <Tag color="red">Удален</Tag> : null}
                    </div>
                  </div>
                  <div className="db-block__actions spr-sotrs__actions" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Копировать">
                      <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => copySotr(id)} />
                    </Tooltip>
                    <Tooltip title="Пароль">
                      <Button
                        type="text"
                        size="small"
                        icon={<KeyOutlined />}
                        onClick={() => {
                          setPasswordUserId(id);
                          setPasswordOpen(true);
                        }}
                      />
                    </Tooltip>
                    <Tooltip title="Удалить">
                      <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => deleteSotr(id)} />
                    </Tooltip>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Spin>

      <SprSotr
        open={editorOpen}
        idSotr={editingId}
        onClose={() => setEditorOpen(false)}
        onSaved={async () => {
          setEditorOpen(false);
          await loadItems();
        }}
      />

      <Modal
        title="Установить пароль"
        open={passwordOpen}
        okText="Сохранить"
        cancelText="Отмена"
        onOk={savePassword}
        onCancel={() => {
          setPasswordOpen(false);
          setPasswordUserId(null);
          setPassword("");
        }}
      >
        <Input.Password value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Новый пароль" />
      </Modal>
    </div>
  );
};

export default SprSotrs;
